import type { JSX } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import { AuthRoute } from 'features/auth';
import { MATCH_MODE_ANY, RoutePermissionSet } from 'features/auth/utils/permissions';
import { CloneTab, CreateMasterPage, CreateSubProjectPage, ManageMasterPage } from 'features/master';
import ProjectsIndexRoute from 'features/projects/routes/ProjectsIndexRoute';
import ChangePasswordPage from 'pages/ChangePasswordPage';
import DemoChatPage from 'pages/DemoChatPage';
import DescripcionesPage from 'pages/DescripcionesPage';
import LoginPage from 'pages/LoginPage';
import MasterConfigPage from 'pages/MasterConfigPage';
import MortgageDocumentsPage from 'pages/MortgageDocumentsPage';
import MortgagePage from 'pages/MortgagePage';
import MultimediaPage from 'pages/MultimediaPage';
import ProjectConfigPage from 'pages/ProjectConfigPage';
import ProjectIntegrationsPage from 'pages/ProjectIntegrationsPage';
import ProjectPromptPage from 'pages/ProjectPromptPage';
import PromptVersionPage from 'pages/PromptVersionPage';
import PropiedadesPage from 'pages/PropiedadesPage';
import UsersPage from 'pages/UsersPage';
import { CrmIntegrationsPage } from 'pages/crm-integrations';
import {
  NotificationCenterActivityPage,
  NotificationCenterPage,
  NotificationCenterRulesPage,
  NotificationCenterSettingsPage,
  NotificationCenterWebhooksPage,
} from 'pages/notification-center';
import {
  TestingCenterPage,
  TestingCenterRunsPage,
  TestingCenterScenariosPage,
  TestingCenterCasesPage,
} from 'pages/testing-center';
import { AuthLayout } from 'widgets/auth-layout';
import { BackofficeLayout } from 'widgets/backoffice-layout';

const BACKOFFICE_ACCESS_PERMISSIONS: RoutePermissionSet = {
  allow: ['backoffice.read', 'backoffice.manage'],
  matchMode: MATCH_MODE_ANY,
};

const App = (): JSX.Element => (
  <Routes>
    <Route element={<AuthLayout />}>
      <Route path="/login" element={<LoginPage />} />
    </Route>
    <Route element={<AuthRoute permissions={BACKOFFICE_ACCESS_PERMISSIONS} />}>
      <Route element={<BackofficeLayout />}>
        <Route path="/" element={<Navigate to="/project" replace />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/project" element={<ProjectsIndexRoute />} />
        <Route path="/project/create" element={<CreateMasterPage />} />
        <Route path="/project/:masterProjectId/createSubProject" element={<CreateSubProjectPage />} />
        <Route path="/project/:masterProjectId/config/:subProjectId" element={<ProjectConfigPage />} />
        <Route path="/project/:masterProjectId/prompt" element={<ProjectPromptPage />} />
        <Route path="/project/:masterProjectId/prompt/new-version" element={<PromptVersionPage />} />
        <Route path="/project/:masterProjectId/prompt/:subProjectId" element={<ProjectPromptPage />} />
        <Route path="/project/:masterProjectId/prompt/:subProjectId/new-version" element={<PromptVersionPage />} />
        <Route path="/project/:masterProjectId/integrations" element={<ProjectIntegrationsPage />} />
        {/* Direct URL only: on purpose, no menu or button links to it */}
        <Route path="/project/:masterProjectId/crm-integrations" element={<CrmIntegrationsPage />} />
        <Route path="/project/:masterProjectId/multimedia" element={<MultimediaPage />} />
        <Route path="/project/:masterProjectId/multimedia/:subProjectId" element={<MultimediaPage />} />
        <Route path="/project/:masterProjectId/mortgage">
          <Route index element={<Navigate to="config" replace />} />
          <Route path="config" element={<MortgagePage />} />
          <Route path="documents" element={<MortgageDocumentsPage />} />
        </Route>
        <Route path="/project/:masterProjectId/propiedades/:subProjectId" element={<PropiedadesPage />} />
        <Route path="/project/:masterProjectId/demo-chat" element={<DemoChatPage />} />
        <Route path="/project/:masterProjectId/descripciones" element={<DescripcionesPage />} />
        <Route path="/project/:masterProjectId/notifications" element={<NotificationCenterPage />}>
          <Route index element={<Navigate to="activity" replace />} />
          <Route path="activity" element={<NotificationCenterActivityPage />} />
          <Route path="rules" element={<NotificationCenterRulesPage />} />
          <Route path="settings" element={<NotificationCenterSettingsPage />} />
          <Route path="webhooks" element={<NotificationCenterWebhooksPage />} />
        </Route>
        <Route path="/project/:masterProjectId/testing" element={<TestingCenterPage />}>
          <Route index element={<Navigate to="casos" replace />} />
          <Route path="casos" element={<TestingCenterCasesPage />} />
          <Route path="escenarios" element={<TestingCenterScenariosPage />} />
          <Route path="runs" element={<TestingCenterRunsPage />} />
        </Route>
        <Route path="/project/:masterProjectId/users" element={<UsersPage />} />
        <Route path="/project/:masterProjectId" element={<ManageMasterPage />}>
          <Route index element={<Navigate to="config" replace />} />
          <Route path="config" element={<MasterConfigPage />} />
          <Route path="clone" element={<CloneTab />} />
          {/* <Route path="rules" element={<MasterRulesPage />} /> */}
        </Route>
        <Route path="/multimedia" element={<Navigate to="/project" replace />} />
        <Route path="/master/create" element={<Navigate to="/project/create" replace />} />
        <Route path="/master/sub/create" element={<Navigate to="/project" replace />} />
        <Route path="/master/manage/*" element={<Navigate to="/project" replace />} />
      </Route>
    </Route>
  </Routes>
);

export default App;
